"use client"

import Link from "next/link"
import { useState, useEffect, useRef } from "react"
import { supabase } from "@/lib/supabase/client"
import { LayoutDashboard, LogOut, Upload, ChevronDown, Menu, X, ArrowRight, Search, HelpCircle, ChevronRight, CreditCard, Settings } from "lucide-react"
import { BridgeIcon } from "@/components/brand/BridgeIcon"
import { PlanBadge, type Plan } from "@/components/ui/PlanBadge"
import { ProductMenu, type ProductMenuItem } from "@/components/header/ProductMenu"
import { useAuth } from "@/lib/auth-context"
import { usePathname } from "next/navigation"
import { useUploadContext } from "@/lib/upload/upload-context"
import UploadStatusChip from "@/components/dashboard/UploadStatusChip"

// ===============================
// LOGO
// ===============================
export function DataBridgeLogo() {
  return (
    <span className="flex items-center gap-3 select-none">
      {/* Badge — Aperture-Light emerald. Deep gradient corner-to-corner
          with inner gloss + bottom depth, matched ring + shadow tints. */}
      <span className="relative flex h-[30px] w-[30px] items-center justify-center rounded-xl bg-gradient-to-br from-[#1F7A4E] via-[#0F5132] to-[#0A3D26] shadow-md shadow-[#0F5132]/40 ring-1 ring-white/[0.18]">
        {/* Inner gloss — light from top */}
        <span className="absolute inset-0 rounded-xl bg-gradient-to-b from-white/[0.18] via-transparent to-transparent" />
        {/* Bottom depth */}
        <span className="absolute inset-0 rounded-xl bg-gradient-to-t from-black/[0.18] via-transparent to-transparent" />
        <BridgeIcon size={18} className="text-white relative z-10 drop-shadow-sm" />
      </span>

      {/* Wordmark — "Bridge" gets an emerald → mint → emerald gradient,
          same shimmer pair used by the headline accent. Stays in sync
          with the rest of the palette. */}
      <span className="leading-none tracking-[-0.45px]">
        <span className="text-[15.5px] font-medium text-[rgba(15,13,10,0.72)]">Data</span>
        <span className="text-[15.5px] font-extrabold bg-gradient-to-r from-[#0F5132] via-[#34D399] to-[#0F5132] bg-clip-text text-transparent">Bridge</span>
      </span>
    </span>
  )
}

// ===============================
// HEADER
// ===============================
export default function Header() {
  const [openDropdown, setOpenDropdown] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  // Mobile drawer collapsibles. Tracks the label of the menu currently
  // expanded — null means everything is collapsed (the default state
  // when the drawer opens). Only one section open at a time so the
  // drawer never balloons past the viewport on a phone.
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState<string | null>(null)
  const { user, plan, openAuth } = useAuth()
  const pathname = usePathname()
  const { status, progress } = useUploadContext()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isLanding   = pathname === "/"
  const isDashboard = pathname?.startsWith("/dashboard") ?? false

  // Lock body scroll while the mobile drawer is open. iOS Safari ignores
  // a plain overflow:hidden on body — it keeps responding to touch
  // gestures. The reliable cross-browser pattern is position:fixed on
  // body anchored at -scrollY, then restore the scroll position when
  // we let go.
  // Collapse the Product accordion every time the drawer closes so
  // the next open starts in its default state (all sections collapsed).
  useEffect(() => {
    if (!mobileMenuOpen) setMobileExpandedMenu(null)
  }, [mobileMenuOpen])

  useEffect(() => {
    if (!mobileMenuOpen) return
    const scrollY = window.scrollY
    const prev = {
      position: document.body.style.position,
      top:      document.body.style.top,
      left:     document.body.style.left,
      right:    document.body.style.right,
      width:    document.body.style.width,
      overflow: document.body.style.overflow,
    }
    document.body.style.position = "fixed"
    document.body.style.top      = `-${scrollY}px`
    document.body.style.left     = "0"
    document.body.style.right    = "0"
    document.body.style.width    = "100%"
    document.body.style.overflow = "hidden"

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false)
    }
    document.addEventListener("keydown", onKey)

    return () => {
      document.body.style.position = prev.position
      document.body.style.top      = prev.top
      document.body.style.left     = prev.left
      document.body.style.right    = prev.right
      document.body.style.width    = prev.width
      document.body.style.overflow = prev.overflow
      window.scrollTo(0, scrollY)
      document.removeEventListener("keydown", onKey)
    }
  }, [mobileMenuOpen])

  // Close drawer on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [pathname])

  // Hash hrefs scroll on the landing page and hard-route to "/#…" on
  // any other route (handled by resolveHref). Make sure each hash maps
  // to a real <section id="…"> on app/page.tsx — orphan hashes were
  // showing as nav links that did nothing.
  //
  // Schema:
  //   - kind:"link" → flat link in the top nav
  //   - kind:"menu" → opens a card-style popover (ProductMenu)
  // Mobile flattens menus into sectioned lists so nothing's hidden.
  type NavItem =
    | { kind: "link"; label: string; href: string }
    | { kind: "menu"; label: string; items: ProductMenuItem[] }

  const navLinks: NavItem[] = [
    {
      kind:  "menu",
      label: "Product",
      items: [
        { label: "Email Cleaner", href: "#how-it-works", description: "Clean, repair, and audit every address."       },
        { label: "Export Mapper", href: "#mapper",       description: "Prepare clean columns for your ESP."           },
        { label: "Compare Files", href: "#compare-files", description: "Find shared, new, and missing contacts."       },
        { label: "Validators",    href: "#checks",        description: "See the nine checks, line by line."            },
      ],
    },
    { kind: "link", label: "Pricing",   href: "#pricing"   },
    { kind: "link", label: "Changelog", href: "/changelog" },
    { kind: "link", label: "Docs",      href: "/docs"      },
  ]

  const handleNavClick = (href: string) => (e: React.MouseEvent) => {
    setMobileMenuOpen(false)
    if (!href.startsWith("#")) return
    // If we're not on the landing page, let the anchor's natural
    // navigation happen — it's already prefixed with "/" so it will
    // hard-route to /#section. preventDefault here would strand the
    // user on /pricing or /docs with nothing happening (the target
    // section only exists on /).
    if (pathname !== "/") return
    e.preventDefault()
    // On landing: wait for the drawer to close, then smooth scroll.
    // Manual offset (header + banner + a comfortable inset) so the
    // badge of each section lands near the top of the viewport
    // instead of below the section's outer padding.
    requestAnimationFrame(() => {
      const id = href.slice(1)
      const el = document.getElementById(id)
      if (!el) return
      const headerOffset =
        64 + // <Header /> height
        (parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--promo-banner-h")) || 0)
      const innerInset = -16 // negative → scroll a bit PAST the section top so the badge sits high under the header instead of leaving empty space above it
      const target = el.getBoundingClientRect().top + window.scrollY - headerOffset - innerInset
      window.scrollTo({ top: Math.max(target, 0), behavior: "smooth" })
      history.replaceState(null, "", href)
    })
  }

  // Hash-link href is "#section" on the landing page (smooth-scroll)
  // and "/#section" on every other route (hard-routes back to landing
  // and lands on the anchor). Without this, header links pointing at
  // hash sections were dead on /pricing, /docs, /changelog.
  const resolveHref = (href: string) =>
    href.startsWith("#") && pathname !== "/" ? `/${href}` : href
  const hidden =
    pathname?.startsWith("/auth/") ||
    pathname === "/login" ||
    pathname?.startsWith("/admin") ||
    pathname?.startsWith("/preview")

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(false)
      }
    }
    if (openDropdown) document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [openDropdown])

  async function handleLogout() {
    setOpenDropdown(false)
    await supabase.auth.signOut()
    window.location.href = "/"
  }

  const handleUploadClick = () => {
    // Always open the global file picker. Earlier the landing branch
    // scrolled to <section id="upload"> because the hero hosted the
    // entire upload pipeline; that surface moved to /try, so the
    // scroll-and-stop behaviour rendered the button as a no-op on
    // production. GlobalUploadInput (rendered from app/layout.tsx)
    // listens for this event from every route.
    window.dispatchEvent(new Event("open-global-upload"))
  }

  const initials = user?.email ? user.email[0].toUpperCase() : "?"

  // ── Landing chrome behavior: transparent at top, glass after scroll.
  // Premium SaaS pattern (Linear, Resend, Vercel) — the hero gets to
  // breathe without a hard chrome line cutting across it, then the
  // header materializes as you scroll into the body of the page.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    if (!isLanding) return
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [isLanding])

  // ── Active landing section, derived from IntersectionObserver.
  // Each landing nav link with a hash href highlights when its section
  // is the dominant one in the viewport. Anchors orientation as the
  // user scrolls.
  const [activeSection, setActiveSection] = useState<string>("")
  useEffect(() => {
    if (!isLanding) return
    // Flatten the nav: collect every hash href whether it's a top-level
    // link or buried inside a Product-menu sub-item, so the active
    // indicator still tracks scroll regardless of where the link lives.
    const ids = navLinks
      .flatMap((l) =>
        l.kind === "link"
          ? [l.href]
          : l.items.map((sub) => sub.href),
      )
      .filter((href) => href.startsWith("#"))
      .map((href) => href.slice(1))
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el)
    if (els.length === 0) return

    // Pick the section whose top is closest to (but past) the header
    // bottom. We use a top-anchored rootMargin so the link flips at the
    // moment the section's eyebrow lands under the header — feels
    // exactly synced rather than triggering halfway through.
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActiveSection(visible[0].target.id)
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
    // navLinks is a stable inline literal but eslint can't see that;
    // we only re-run if isLanding flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLanding])

  // ── Dashboard breadcrumb derived from pathname.
  // Each crumb carries an `href` so intermediate levels are clickable
  // ("Datasets > Detail" → click "Datasets" jumps back to the list,
  // mirroring Linear/Stripe). Last crumb is rendered as plain text.
  const breadcrumb: { label: string; href: string }[] = (() => {
    if (!pathname?.startsWith("/dashboard")) return []
    const segs = pathname.split("/").filter(Boolean).slice(1) // drop "dashboard"
    if (segs.length === 0) return [{ label: "Overview", href: "/dashboard" }]
    const titleMap: Record<string, string> = {
      datasets: "Datasets",
      compare:  "Compare",
      mapper:   "Mapper",
      trash:    "Trash",
      billing:  "Billing",
      settings: "Settings",
      api:      "API",
    }
    return segs.map((s, i) => {
      // Raw IDs (UUIDs / 8+ char slugs) → "Detail"
      const label =
        i > 0 && /^[a-f0-9-]{8,}$/i.test(s)
          ? "Detail"
          : titleMap[s] ?? s.charAt(0).toUpperCase() + s.slice(1)
      const href = "/dashboard/" + segs.slice(0, i + 1).join("/")
      return { label, href }
    })
  })()

  const planAccentBadge: Record<string, string> = {
    free:   "text-[rgba(15,13,10,0.60)] bg-[rgba(15,13,10,0.04)] border-[rgba(15,13,10,0.10)] hover:bg-[rgba(15,13,10,0.05)]",
    pro:    "text-[#0F5132] bg-[#DFF5E5] border-[#0F5132]/30 hover:bg-[#0F5132]/[0.10]",
    agency: "text-white bg-[#0A0A0A] border-[#0A0A0A] hover:bg-[#1A1A1A]",
  }
  const planLabel = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "Free"

  const handleCmdK = () => {
    window.dispatchEvent(new Event("open-command-palette"))
  }
  // Note: ⌘K keydown is bound inside CommandPalette itself, so the
  // topbar button only needs to dispatch the open-event. Adding a
  // second listener here caused a double-toggle (open + close).

  if (hidden) return null

  return (
    <>
    <header className="fixed left-0 right-0 z-50" style={{ top: "var(--promo-banner-h, 0px)" }}>
      {/* Glass layer — on landing it's invisible at top of page and
          fades in as the user scrolls past the hero. Everywhere else
          (dashboard, /pricing, etc.) it stays solid so the chrome is
          always anchored. */}
      <div
        className={`
          absolute inset-0
          transition-[border-color,box-shadow] duration-300 ease-out
          border-b border-[rgba(15,13,10,0.12)]
          ${isLanding && !scrolled
            ? ""
            : "shadow-[0_1px_0_0_rgba(15,13,10,0.04)]"}
        `}
        style={{ background: "#FAF7EE" }}
      />

      {/* Container layout adapts to context:
          - Marketing (landing + /pricing + /docs + /changelog + /try):
            1240px centered, matches the hero + aperture/_shared MAXW
            so the header chrome aligns perfectly with every section
            below it. Earlier this was 1400 on landing only and full-
            width everywhere else — both wrong: the 1400 made the
            header CTAs drift ~160px past the hero content on wide
            monitors, and the full-width fallback on /pricing /docs
            etc. left the same chrome looking entirely different
            across pages in the same nav.
          - Dashboard: full-width, no max-w. The logo anchors to the
            left edge (sitting visually above the sidebar) and the
            user controls anchor to the right edge, matching the
            standard dashboard chrome pattern (Stripe, Vercel,
            Linear). */}
      <div
        className={`
          relative flex items-center justify-between px-4 md:px-6
          transition-[height] duration-300 ease-out
          ${isDashboard
            ? "h-12"
            : isLanding && scrolled
            ? "h-14"
            : "h-16"}
          ${!isDashboard ? "max-w-[1240px] mx-auto" : ""}
        `}
      >

        {/* LEFT — hamburger (mobile) + logo + nav */}
        <div className="flex items-center gap-3 md:gap-8">
          {/* Hamburger — dashboard opens sidebar, every other route
              (landing + /pricing + /docs + /changelog + /try) opens
              the nav drawer. */}
          {isDashboard ? (
            <button
              onClick={() => window.dispatchEvent(new Event("open-sidebar"))}
              className="md:hidden p-1.5 rounded-lg text-[rgba(15,13,10,0.55)] hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)] transition"
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
          ) : (
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-1.5 rounded-lg text-[rgba(15,13,10,0.60)] hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)] transition"
              aria-label="Open navigation"
            >
              <Menu size={18} />
            </button>
          )}

          {isDashboard ? (
            // Dashboard chrome: small icon + wordmark act as the brand
            // anchor (the desktop sidebar no longer carries it). A thin
            // vertical rule then visually separates "brand" from
            // "current location" so the breadcrumb doesn't read as if
            // "DataBridge" were a parent crumb.
            <div className="flex items-center gap-3 min-w-0">
              <Link
                href="/"
                className="group flex items-center gap-2 min-w-0 flex-shrink-0"
                aria-label="DataBridge home"
              >
                <span className="relative flex h-[22px] w-[22px] items-center justify-center rounded-md bg-gradient-to-br from-[#1F7A4E] via-[#0F5132] to-[#0A3D26] ring-1 ring-white/[0.13] shadow-md shadow-[#0F5132]/30 transition-transform duration-200 group-hover:scale-[1.04]">
                  <BridgeIcon size={13} className="text-white" />
                </span>
                <span className="hidden sm:inline text-[13px] font-semibold tracking-tight text-[rgba(15,13,10,0.80)] group-hover:text-[#0F5132] transition-colors">
                  DataBridge
                </span>
              </Link>

              {breadcrumb.length > 0 && (
                <>
                  <span className="hidden sm:block w-px h-4 bg-[rgba(15,13,10,0.08)]" aria-hidden />
                  <nav
                    aria-label="Breadcrumb"
                    className="flex items-center gap-1.5 min-w-0"
                  >
                    {breadcrumb.map((crumb, i) => {
                      const isLast = i === breadcrumb.length - 1
                      return (
                        <span key={i} className="flex items-center gap-1.5 min-w-0">
                          {i > 0 && (
                            <ChevronRight size={11} className="text-[rgba(15,13,10,0.30)] flex-shrink-0" />
                          )}
                          {isLast ? (
                            <span
                              className="text-[13px] truncate text-[rgba(15,13,10,0.85)] font-medium"
                              aria-current="page"
                            >
                              {crumb.label}
                            </span>
                          ) : (
                            <Link
                              href={crumb.href}
                              className="text-[13px] truncate text-[rgba(15,13,10,0.45)] hover:text-[rgba(15,13,10,0.75)] transition-colors"
                            >
                              {crumb.label}
                            </Link>
                          )}
                        </span>
                      )
                    })}
                  </nav>
                </>
              )}
            </div>
          ) : (
            <Link href="/" className="flex items-center">
              <DataBridgeLogo />
            </Link>
          )}

          {/* Desktop nav links — render on every marketing route, not
              just landing. Hash links smooth-scroll on /; on /pricing,
              /docs, /changelog they hard-route back to /#section via
              resolveHref so the user can always reach How it works,
              Validators, etc. from any marketing page. */}
          {!isDashboard && (
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((item) => {
                if (item.kind === "menu") {
                  return (
                    <ProductMenu
                      key={item.label}
                      label={item.label}
                      items={item.items}
                      resolveHref={resolveHref}
                      onItemClick={(href, e) => handleNavClick(href)(e)}
                    />
                  )
                }
                const isHash    = item.href.startsWith("#")
                const isActive  = isHash && activeSection === item.href.slice(1)
                return (
                  <a
                    key={item.label}
                    href={resolveHref(item.href)}
                    onClick={handleNavClick(item.href)}
                    aria-current={isActive ? "true" : undefined}
                    className={`
                      group relative inline-flex items-center
                      px-3 py-1.5 text-sm
                      transition-colors duration-150
                      ${isActive ? "text-[#0F5132]" : "text-[rgba(15,13,10,0.60)] hover:text-[#0F5132]"}
                    `}
                  >
                    {item.label}
                    {/* Animated underline — grows from center on hover,
                        anchored full-width when the section is active. */}
                    <span
                      aria-hidden
                      className={`
                        pointer-events-none
                        absolute left-3 right-3 -bottom-0.5 h-px
                        bg-gradient-to-r from-[#1F7A4E] via-[#34D399] to-[#0F5132]
                        origin-center
                        transition-transform duration-200 ease-out
                        ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}
                      `}
                    />
                  </a>
                )
              })}
            </nav>
          )}
        </div>

        {/* RIGHT */}
        <div className="flex items-center gap-1.5 md:gap-2" ref={dropdownRef}>

          {/* JOB PROGRESS CHIP — Vercel-style "deployments" indicator.
              Compact in the topbar, expands into a rich popover (or
              full-width bottom sheet on mobile) on click.
              Mounted on EVERY route (not just dashboard) so an upload
              triggered from the dashboard keeps surfacing its progress
              when the user wanders off to /pricing, /docs, or the
              landing page. The chip self-hides when status === 'idle'
              (UploadStatusChip returns null in that case), so
              non-upload sessions don't pay any visual cost. */}
          {user && <UploadStatusChip />}

          {/* DASHBOARD CHROME — ⌘K · Help · Plan.
              Three utilities share the same height + hover treatment so
              they read as one tool family, not three floating buttons. */}
          {isDashboard && user && (
            <>
              {/* ⌘K — opens the existing CommandPalette.
                  Compact: icon + kbd, no "Search" word (Linear-style). */}
              <button
                onClick={handleCmdK}
                className="
                  group inline-flex items-center gap-1.5
                  h-8 px-2 rounded-lg
                  text-[rgba(15,13,10,0.55)] hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                  transition-colors duration-150 cursor-pointer
                "
                aria-label="Search (Cmd Shift K)"
                title="Search"
              >
                <Search size={14} />
                <span className="hidden md:inline-flex items-center gap-0.5">
                  <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded border border-[rgba(15,13,10,0.10)] bg-[rgba(15,13,10,0.03)] text-[9.5px] font-mono text-[rgba(15,13,10,0.60)] group-hover:text-[rgba(15,13,10,0.75)] transition-colors">⌘</span>
                  <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded border border-[rgba(15,13,10,0.10)] bg-[rgba(15,13,10,0.03)] text-[9.5px] font-mono text-[rgba(15,13,10,0.60)] group-hover:text-[rgba(15,13,10,0.75)] transition-colors">⇧</span>
                  <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded border border-[rgba(15,13,10,0.10)] bg-[rgba(15,13,10,0.03)] text-[9.5px] font-mono text-[rgba(15,13,10,0.60)] group-hover:text-[rgba(15,13,10,0.75)] transition-colors">K</span>
                </span>
              </button>

              {/* Help / Docs */}
              <Link
                href="/docs"
                className="
                  inline-flex items-center justify-center w-8 h-8 rounded-lg
                  text-[rgba(15,13,10,0.55)] hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                  transition-colors duration-150
                "
                aria-label="Help & docs"
                title="Help & docs"
              >
                <HelpCircle size={15} />
              </Link>

              {/* Subtle vertical rule before the plan badge — visually
                  separates "tools" from "status". */}
              <span className="hidden sm:block w-px h-4 bg-[rgba(15,13,10,0.06)] mx-0.5" aria-hidden />

              {/* Plan affordance — context-aware:
                  - Free users: emerald "Upgrade" CTA that jumps STRAIGHT
                    to /checkout?plan=pro. The old FREE badge routed to
                    /dashboard/billing, which for a free user is a dead
                    end (nothing to manage there yet) and forced an
                    extra click before the actual upgrade form. Direct
                    checkout shortens the conversion path to one tap
                    from any dashboard route.
                  - Paid users (pro / agency): keep the plan badge that
                    routes to /dashboard/billing — that page is useful
                    for them (manage seats, invoices, change plan). */}
              {plan === "free" ? (
                <Link
                  href="/checkout?plan=pro"
                  data-testid="header-upgrade-cta"
                  className="
                    inline-flex items-center gap-1 h-8 pl-3 pr-2.5 rounded-full
                    text-[11.5px] font-bold tracking-wide uppercase
                    bg-gradient-to-r from-[#0F5132] to-[#0A3D26]
                    hover:from-[#1F7A4E] hover:to-[#0F5132]
                    text-[#FAF7EE] shadow-sm shadow-[#0F5132]/30
                    transition-all duration-150
                  "
                  title="Upgrade to Pro"
                >
                  Upgrade
                  <ArrowRight size={11} strokeWidth={2.5} />
                </Link>
              ) : (
                <Link
                  href="/dashboard/billing"
                  className={`
                    inline-flex items-center h-8 px-3 rounded-full
                    border text-[11.5px] font-semibold tracking-wide uppercase
                    transition-colors duration-150
                    ${planAccentBadge[plan] ?? planAccentBadge.free}
                  `}
                  title={`${planLabel} plan — manage billing`}
                >
                  {planLabel}
                </Link>
              )}
            </>
          )}


          {/* Upload — mobile icon (only for logged-in users; redundant on dashboard where sidebar has New CSV) */}
          {user && !isDashboard && (
            <button
              onClick={handleUploadClick}
              disabled={status === "processing"}
              aria-label="Upload CSV"
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-xl bg-[#0F5132]/12 hover:bg-[#0F5132]/20 border border-[#0F5132]/25 text-[#0F5132] transition-all duration-150 disabled:opacity-50"
            >
              {status === "processing" ? (
                <span className="text-[10px] font-bold tabular-nums">{Math.floor(progress)}%</span>
              ) : (
                <Upload size={15} />
              )}
            </button>
          )}

          {/* DESKTOP */}
          <div className="hidden md:flex items-center gap-2">

            {!user ? (
              <>
                <button
                  onClick={() => openAuth("login")}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-[rgba(15,13,10,0.62)] hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)] transition-all duration-150"
                >
                  Log in
                </button>
                {/* Primary CTA for anonymous visitors — opens signup directly */}
                <button
                  onClick={() => openAuth("signup")}
                  className="
                    relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold
                    bg-gradient-to-r from-[#0F5132] to-[#0A3D26]
                    hover:from-[#1F7A4E] hover:to-[#0F5132]
                    text-white shadow-lg shadow-[#0F5132]/30
                    transition-all duration-200
                  "
                >
                  Get started free
                </button>
              </>
            ) : isDashboard ? (
              /* On dashboard, user controls + upload live in the sidebar.
                 Keep the topbar lean (just brand + nav) to avoid duplication. */
              null
            ) : (
              <>
                {/* User dropdown trigger — wrapped in a relative div so
                    the menu anchors to THIS button (not the whole right
                    cluster, which used to drift the menu under "Upload"). */}
                <div className="relative">
                  <button
                    onClick={() => setOpenDropdown((v) => !v)}
                    className="
                      group flex items-center gap-2 pl-1.5 pr-2.5 py-1
                      rounded-full
                      border border-[rgba(15,13,10,0.10)] bg-[rgba(15,13,10,0.03)]
                      hover:bg-[rgba(15,13,10,0.04)] hover:border-[rgba(15,13,10,0.14)]
                      transition-colors duration-150 cursor-pointer
                    "
                    aria-haspopup="menu"
                    aria-expanded={openDropdown}
                  >
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#1F7A4E] via-[#0F5132] to-[#0A3D26] text-[12px] font-bold text-white leading-none ring-1 ring-[rgba(15,13,10,0.10)] shadow-sm shadow-[#0F5132]/30">
                      {initials}
                    </span>
                    <span className="text-[13px] font-medium text-[rgba(15,13,10,0.75)] group-hover:text-[#0F5132] max-w-[120px] truncate transition-colors">
                      {user.email?.split("@")[0]}
                    </span>
                    <ChevronDown
                      size={12}
                      className={`text-[rgba(15,13,10,0.42)] group-hover:text-[rgba(15,13,10,0.65)] transition-all duration-200 ${openDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {/* Dropdown menu — anchored to the trigger above */}
                  {openDropdown && (
                    <div
                      role="menu"
                      className="
                        absolute right-0 top-[calc(100%+8px)] z-50
                        w-64 rounded-2xl overflow-hidden
                        border border-[rgba(15,13,10,0.10)]
                        bg-[#F2EEE3]/95 backdrop-blur-xl
                        shadow-2xl shadow-black/60
                        animate-in fade-in slide-in-from-top-2 duration-150
                      "
                    >
                      {/* Top accent line — matches the chip family */}
                      <div className="h-px bg-gradient-to-r from-transparent via-[#0F5132]/30 to-transparent" />

                      {/* Identity row */}
                      <div className="flex items-center gap-3 px-3.5 pt-3.5 pb-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#1F7A4E] via-[#0F5132] to-[#0A3D26] text-[14px] font-bold text-white leading-none ring-1 ring-[rgba(15,13,10,0.10)] shadow shadow-[#0F5132]/30 flex-shrink-0">
                          {initials}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[12.5px] font-semibold text-[#0A0A0A] truncate">
                            {user.email?.split("@")[0]}
                          </p>
                          <p className="text-[11px] text-[rgba(15,13,10,0.45)] truncate">
                            {user.email}
                          </p>
                        </div>
                        {plan && plan !== "free" && (
                          <PlanBadge plan={plan as Plan} size="sm" />
                        )}
                      </div>

                      <div className="h-px bg-[rgba(15,13,10,0.04)]" />

                      {/* Primary items */}
                      <div className="p-1.5">
                        <Link
                          href="/dashboard"
                          onClick={() => setOpenDropdown(false)}
                          role="menuitem"
                          className="
                            group flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                            text-[13px] text-[rgba(15,13,10,0.72)]
                            hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                            transition-colors duration-150
                          "
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-md bg-[rgba(15,13,10,0.03)] border border-[rgba(15,13,10,0.08)] text-[rgba(15,13,10,0.50)] group-hover:text-[rgba(15,13,10,0.75)] group-hover:bg-[rgba(15,13,10,0.04)] transition-colors">
                            <LayoutDashboard size={12} strokeWidth={2} />
                          </span>
                          <span className="flex-1">Dashboard</span>
                        </Link>

                        <Link
                          href="/dashboard/billing"
                          onClick={() => setOpenDropdown(false)}
                          role="menuitem"
                          className="
                            group flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                            text-[13px] text-[rgba(15,13,10,0.72)]
                            hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                            transition-colors duration-150
                          "
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-md bg-[rgba(15,13,10,0.03)] border border-[rgba(15,13,10,0.08)] text-[rgba(15,13,10,0.50)] group-hover:text-[rgba(15,13,10,0.75)] group-hover:bg-[rgba(15,13,10,0.04)] transition-colors">
                            <CreditCard size={12} strokeWidth={2} />
                          </span>
                          <span className="flex-1">Billing</span>
                        </Link>

                        <Link
                          href="/dashboard/settings"
                          onClick={() => setOpenDropdown(false)}
                          role="menuitem"
                          className="
                            group flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                            text-[13px] text-[rgba(15,13,10,0.72)]
                            hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                            transition-colors duration-150
                          "
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-md bg-[rgba(15,13,10,0.03)] border border-[rgba(15,13,10,0.08)] text-[rgba(15,13,10,0.50)] group-hover:text-[rgba(15,13,10,0.75)] group-hover:bg-[rgba(15,13,10,0.04)] transition-colors">
                            <Settings size={12} strokeWidth={2} />
                          </span>
                          <span className="flex-1">Settings</span>
                        </Link>

                        <button
                          onClick={() => {
                            setOpenDropdown(false)
                            requestAnimationFrame(() => window.dispatchEvent(new Event("open-support")))
                          }}
                          role="menuitem"
                          className="
                            group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                            text-[13px] text-[rgba(15,13,10,0.72)] text-left
                            hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                            transition-colors duration-150 cursor-pointer
                          "
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-md bg-[rgba(15,13,10,0.03)] border border-[rgba(15,13,10,0.08)] text-[rgba(15,13,10,0.50)] group-hover:text-[rgba(15,13,10,0.75)] group-hover:bg-[rgba(15,13,10,0.04)] transition-colors">
                            <HelpCircle size={12} strokeWidth={2} />
                          </span>
                          <span className="flex-1">Support</span>
                        </button>
                      </div>

                      <div className="h-px bg-[rgba(15,13,10,0.04)]" />

                      {/* Sign out */}
                      <div className="p-1.5">
                        <button
                          onClick={handleLogout}
                          role="menuitem"
                          className="
                            group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                            text-[13px] text-rose-700/85 text-left
                            hover:text-rose-200 hover:bg-rose-500/[0.08]
                            transition-colors duration-150 cursor-pointer
                          "
                        >
                          <span className="w-6 h-6 flex items-center justify-center rounded-md bg-rose-500/[0.08] border border-rose-500/[0.18] text-rose-700/85 group-hover:text-rose-200 group-hover:bg-rose-500/15 transition-colors">
                            <LogOut size={12} strokeWidth={2} />
                          </span>
                          <span className="flex-1">Log out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Upload CTA — only for logged-in users (anonymous visitors see Get started) */}
                <button
                  onClick={handleUploadClick}
                  disabled={status === "processing"}
                  className="
                    group relative flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold
                    bg-gradient-to-r from-[#0F5132] to-[#0A3D26]
                    hover:from-[#1F7A4E] hover:to-[#0F5132]
                    text-white shadow-lg shadow-[#0F5132]/30
                    transition-all duration-200
                    disabled:opacity-60 disabled:cursor-not-allowed
                    overflow-hidden cursor-pointer
                  "
                >
                  {status === "processing" ? (
                    <span className="tabular-nums">{Math.floor(progress)}%</span>
                  ) : (
                    <>
                      <Upload size={13} strokeWidth={2.5} className="transition-transform group-hover:-translate-y-0.5" />
                      Upload CSV
                    </>
                  )}
                </button>
              </>
            )}
          </div>

          {/* User dropdown — mobile avatar (hidden on dashboard: account row lives in sidebar drawer) */}
          {user && !isDashboard ? (
            <div className="md:hidden relative">
              <button
                className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1F7A4E] via-[#0F5132] to-[#0A3D26] text-[13px] font-bold text-white shadow-md ring-1 ring-[rgba(15,13,10,0.10)]"
                onClick={() => setOpenDropdown((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={openDropdown}
              >
                {initials}
              </button>

              {openDropdown && (
                <div
                  role="menu"
                  className="
                    absolute right-0 top-[calc(100%+8px)] z-50
                    w-64 rounded-2xl overflow-hidden
                    border border-[rgba(15,13,10,0.10)]
                    bg-[#F2EEE3]/95 backdrop-blur-xl
                    shadow-2xl shadow-black/60
                    animate-in fade-in slide-in-from-top-2 duration-150
                  "
                >
                  <div className="h-px bg-gradient-to-r from-transparent via-[#0F5132]/30 to-transparent" />
                  <div className="flex items-center gap-3 px-3.5 pt-3.5 pb-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#1F7A4E] via-[#0F5132] to-[#0A3D26] text-[14px] font-bold text-white leading-none ring-1 ring-[rgba(15,13,10,0.10)] shadow shadow-[#0F5132]/30 flex-shrink-0">
                      {initials}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12.5px] font-semibold text-[#0A0A0A] truncate">
                        {user.email?.split("@")[0]}
                      </p>
                      <p className="text-[11px] text-[rgba(15,13,10,0.45)] truncate">
                        {user.email}
                      </p>
                    </div>
                    {plan && plan !== "free" && (
                      <span
                        className={`
                          inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] font-bold tracking-widest uppercase border
                          ${plan === "pro"
                            ? "text-[#0F5132] bg-[#DFF5E5] border-[#0F5132]/30"
                            : "text-white bg-[#0A0A0A] border-[#0A0A0A]"}
                        `}
                      >
                        {plan}
                      </span>
                    )}
                  </div>
                  <div className="h-px bg-[rgba(15,13,10,0.04)]" />
                  <div className="p-1.5">
                    {[
                      { href: "/dashboard",          label: "Dashboard", Icon: LayoutDashboard },
                      { href: "/dashboard/billing",  label: "Billing",   Icon: CreditCard },
                      { href: "/dashboard/settings", label: "Settings",  Icon: Settings },
                    ].map(({ href, label, Icon }) => (
                      <Link
                        key={href}
                        href={href}
                        onClick={() => setOpenDropdown(false)}
                        role="menuitem"
                        className="
                          group flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                          text-[13px] text-[rgba(15,13,10,0.72)]
                          hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                          transition-colors duration-150
                        "
                      >
                        <span className="w-6 h-6 flex items-center justify-center rounded-md bg-[rgba(15,13,10,0.03)] border border-[rgba(15,13,10,0.08)] text-[rgba(15,13,10,0.50)] group-hover:text-[rgba(15,13,10,0.75)] group-hover:bg-[rgba(15,13,10,0.04)] transition-colors">
                          <Icon size={12} strokeWidth={2} />
                        </span>
                        <span className="flex-1">{label}</span>
                      </Link>
                    ))}
                    <button
                      onClick={() => {
                        setOpenDropdown(false)
                        requestAnimationFrame(() => window.dispatchEvent(new Event("open-support")))
                      }}
                      role="menuitem"
                      className="
                        group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                        text-[13px] text-[rgba(15,13,10,0.72)] text-left
                        hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                        transition-colors duration-150 cursor-pointer
                      "
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-md bg-[rgba(15,13,10,0.03)] border border-[rgba(15,13,10,0.08)] text-[rgba(15,13,10,0.50)] group-hover:text-[rgba(15,13,10,0.75)] group-hover:bg-[rgba(15,13,10,0.04)] transition-colors">
                        <HelpCircle size={12} strokeWidth={2} />
                      </span>
                      <span className="flex-1">Support</span>
                    </button>
                  </div>
                  <div className="h-px bg-[rgba(15,13,10,0.04)]" />
                  <div className="p-1.5">
                    <button
                      onClick={handleLogout}
                      role="menuitem"
                      className="
                        group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg
                        text-[13px] text-rose-700/85 text-left
                        hover:text-rose-200 hover:bg-rose-500/[0.08]
                        transition-colors duration-150 cursor-pointer
                      "
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-md bg-rose-500/[0.08] border border-rose-500/[0.18] text-rose-700/85 group-hover:text-rose-200 group-hover:bg-rose-500/15 transition-colors">
                        <LogOut size={12} strokeWidth={2} />
                      </span>
                      <span className="flex-1">Log out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : !user ? (
            /* Anonymous mobile: text Log in (ghost) + primary Get started CTA */
            <>
              <button
                onClick={() => openAuth("login")}
                className="md:hidden px-3 py-2 rounded-xl text-sm font-medium text-[rgba(15,13,10,0.62)] hover:text-[#0F5132] transition-all duration-150"
              >
                Log in
              </button>
              <button
                onClick={() => openAuth("signup")}
                className="md:hidden px-3.5 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#0F5132] to-[#0A3D26] hover:from-[#1F7A4E] hover:to-[#0F5132] text-white shadow-lg shadow-[#0F5132]/30 transition-all duration-150"
              >
                Sign up
              </button>
            </>
          ) : null}
        </div>
      </div>

    </header>

      {/* MOBILE NAV DRAWER — landing only.
          Rendered OUTSIDE <header> so it doesn't inherit the header's
          z-50 stacking context — that's why the previous z-[200]
          inside the header was still capped under the promo banner.
          As a top-level fixed element it stacks correctly above
          everything (banner is z-[60]). */}
      {!isDashboard && mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[200] flex animate-in fade-in duration-150">
          {/* Backdrop — soft ink wash so the cream drawer reads against
              the page behind it without going pitch-black. */}
          <div
            className="absolute inset-0 bg-[rgba(10,10,10,0.32)] backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Drawer panel — anchored to the LEFT, cream surface to match
              the Aperture-Light palette (was dark navy, which clashed
              with everything else and made the dark-ink nav labels
              invisible). */}
          <div className="relative mr-auto w-72 max-w-[85vw] h-full bg-[#FAF7EE] border-r border-[rgba(10,10,10,0.08)] shadow-2xl shadow-black/20 animate-in slide-in-from-left duration-200 flex flex-col">
            {/* Mint hairline at the top — same cue we use on cards */}
            <div
              aria-hidden
              className="absolute inset-x-6 top-0 h-px pointer-events-none"
              style={{ background: "linear-gradient(90deg, transparent, rgba(15,81,50,0.45), transparent)" }}
            />

            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 h-16 border-b border-[rgba(10,10,10,0.08)]">
              <DataBridgeLogo />
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 rounded-lg text-[rgba(15,13,10,0.50)] hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)] transition"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>

            {/* Nav links — menu items collapse into an accordion row by
                default. The drawer was getting tall on phones because
                Product fully expanded shipped 4 sub-items + descriptions
                before the user even tapped anything. Tap to expand. */}
            <nav className="flex-1 overflow-y-auto px-3 py-4">
              {navLinks.map((item, idx) => {
                if (item.kind === "menu") {
                  const expanded = mobileExpandedMenu === item.label
                  return (
                    <div key={item.label} className={idx > 0 ? "mt-4 pt-4 border-t border-[rgba(10,10,10,0.06)]" : ""}>
                      <button
                        type="button"
                        aria-expanded={expanded}
                        aria-controls={`mobile-menu-${item.label}`}
                        onClick={() => setMobileExpandedMenu((curr) => (curr === item.label ? null : item.label))}
                        className="
                          w-full flex items-center justify-between gap-3
                          px-3 py-3 rounded-xl text-base font-medium
                          text-[rgba(15,13,10,0.75)] hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                          transition-colors
                        "
                      >
                        {item.label}
                        <ChevronDown
                          size={16}
                          className="text-[rgba(15,13,10,0.45)] transition-transform duration-200"
                          style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
                        />
                      </button>
                      {expanded && (
                        <ul id={`mobile-menu-${item.label}`} className="mt-1 mb-1 space-y-0.5">
                          {item.items.map((sub) => (
                            <li key={sub.label}>
                              <a
                                href={resolveHref(sub.href)}
                                onClick={handleNavClick(sub.href)}
                                className="
                                  flex items-center justify-between gap-3
                                  pl-6 pr-3 py-2.5 rounded-xl text-[15px] font-medium
                                  text-[rgba(15,13,10,0.75)] hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                                  transition-colors
                                "
                              >
                                <span className="flex flex-col">
                                  <span>{sub.label}</span>
                                  {sub.description ? (
                                    <span className="text-[12px] font-normal text-[rgba(15,13,10,0.45)]">
                                      {sub.description}
                                    </span>
                                  ) : null}
                                </span>
                                <ArrowRight size={14} className="text-[rgba(15,13,10,0.35)] flex-shrink-0" />
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )
                }

                return (
                  <ul
                    key={item.label}
                    className={idx > 0 ? "mt-4 pt-4 border-t border-[rgba(10,10,10,0.06)] space-y-0.5" : "space-y-0.5"}
                  >
                    <li>
                      <a
                        href={resolveHref(item.href)}
                        onClick={handleNavClick(item.href)}
                        className="
                          flex items-center justify-between gap-3
                          px-3 py-3 rounded-xl text-base font-medium
                          text-[rgba(15,13,10,0.75)] hover:text-[#0F5132] hover:bg-[rgba(15,13,10,0.04)]
                          transition-colors
                        "
                      >
                        {item.label}
                        <ArrowRight size={14} className="text-[rgba(15,13,10,0.35)]" />
                      </a>
                    </li>
                  </ul>
                )
              })}
            </nav>

            {/* Footer CTAs */}
            <div className="p-4 border-t border-[rgba(10,10,10,0.08)] space-y-2">
              {!user ? (
                <>
                  <button
                    onClick={() => { setMobileMenuOpen(false); openAuth("login") }}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-medium border border-[rgba(15,13,10,0.10)] bg-[rgba(15,13,10,0.03)] hover:bg-[rgba(15,13,10,0.04)] text-[rgba(15,13,10,0.80)] hover:text-[#0F5132] transition-colors"
                  >
                    Log in
                  </button>
                  <button
                    onClick={() => { setMobileMenuOpen(false); openAuth("signup") }}
                    className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#0F5132] to-[#0A3D26] hover:from-[#1F7A4E] hover:to-[#0F5132] text-white shadow-lg shadow-[#0F5132]/30 transition-all"
                  >
                    Get started free
                  </button>
                </>
              ) : (
                <Link
                  href="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#0F5132] to-[#0A3D26] hover:from-[#1F7A4E] hover:to-[#0F5132] text-white shadow-lg shadow-[#0F5132]/30"
                >
                  <LayoutDashboard size={14} />
                  Go to dashboard
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
